/**
 * @fileoverview UnifiedNameDrawer — a slide-in drawer that shows a Google Maps
 * preview and canonical details for a unified venue.
 *
 * Triggered by clicking the `UnifiedName` value in the Master/Detail table.
 * Displays:
 *  - The venue's `googleUnifiedName`
 *  - Its `formattedAddress`
 *  - An embedded Google Maps iframe (keyed by the formatted address)
 *  - A "View on Google Maps" external link
 */

import * as React from 'react';
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    Link,
    Skeleton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/Place';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link UnifiedNameDrawer}. */
export interface UnifiedNameDrawerProps {
    /** Whether the drawer is open. */
    open: boolean;
    /** The Google unified venue name to display as the drawer title. */
    unifiedName: string;
    /** Full formatted address from Google Places. Used for the map embed. */
    formattedAddress?: string | null;
    /** Google Maps URI for the external link. */
    googleMapsUri?: string | null;
    /** Called when the user closes the drawer. */
    onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * A right-side `Drawer` that renders a map preview for the selected venue.
 *
 * The map is embedded via the classic `maps.google.com?q=…&output=embed` URL
 * (no API key required) using the venue's `formattedAddress`. When no address
 * is available the iframe is omitted and only the text details are shown.
 *
 * @param props.open             - Controls drawer visibility.
 * @param props.unifiedName      - Venue display name (drawer title).
 * @param props.formattedAddress - Address used for the embedded map query.
 * @param props.googleMapsUri    - External "View on Google Maps" link.
 * @param props.onClose          - Close callback.
 *
 * @example
 * <UnifiedNameDrawer
 *   open={drawerOpen}
 *   unifiedName="Bridgestone Arena"
 *   formattedAddress="501 Broadway, Nashville, TN 37203, USA"
 *   googleMapsUri="https://maps.app.goo.gl/abc123"
 *   onClose={() => setDrawerOpen(false)}
 * />
 */
const UnifiedNameDrawer: React.FC<UnifiedNameDrawerProps> = ({
    open,
    unifiedName,
    formattedAddress,
    googleMapsUri,
    onClose,
}) => {
    const [iframeLoaded, setIframeLoaded] = React.useState(false);

    // Reset iframe loading state whenever the drawer opens for a new venue.
    React.useEffect(() => {
        if (open) setIframeLoaded(false);
    }, [open, formattedAddress]);

    const embedSrc = formattedAddress
        ? `https://maps.google.com/maps?q=${encodeURIComponent(formattedAddress)}&output=embed`
        : null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 750 } } }}
        >
            {/* ── Header ──────────────────────────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                }}
            >
                <PlaceIcon color="primary" />
                <Typography variant="h6" sx={{ flex: 1, fontSize: '1rem', fontWeight: 600 }} noWrap>
                    {unifiedName || '(unnamed venue)'}
                </Typography>
                <IconButton size="small" onClick={onClose} aria-label="close drawer">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Divider />

            {/* ── Body ────────────────────────────────────────────────────────── */}
            <Box sx={{ px: 2, py: 1.5 }}>
                {formattedAddress && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {formattedAddress}
                    </Typography>
                )}

                {googleMapsUri?.startsWith('https://') && (
                    <Link
                        href={googleMapsUri}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                        sx={{ display: 'block', mb: 2 }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        View on Google Maps ↗
                    </Link>
                )}

                {/* ── Map embed ─────────────────────────────────────────────── */}
                {embedSrc ? (
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            pt: '66%', // 3:2 aspect ratio
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: 1,
                            borderColor: 'divider',
                        }}
                    >
                        {!iframeLoaded && (
                            <Skeleton
                                variant="rectangular"
                                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                            />
                        )}
                        <Box
                            component="iframe"
                            src={embedSrc}
                            title={`Map preview for ${unifiedName}`}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            onLoad={() => setIframeLoaded(true)}
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                border: 0,
                                opacity: iframeLoaded ? 1 : 0,
                                transition: 'opacity 0.3s ease',
                            }}
                        />
                    </Box>
                ) : (
                    <Typography variant="caption" color="text.disabled">
                        No address available for map preview.
                    </Typography>
                )}
            </Box>
        </Drawer>
    );
};

export default UnifiedNameDrawer;
